import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { AddIconComponent } from '@kuiper/icons';
import { UserDropdownViewComponent } from '../../../../../ui-components/lib/user-dropdown-view/user-dropdown-view.component';
import { Option } from '../../../../common/models/option.model';
import { FeatureFlagsService } from '../../../../common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../../common/constants/feature-flags.constants';

@Component({
  selector: 'lib-collaborator-section',
  imports: [AddIconComponent, TranslocoPipe, UserDropdownViewComponent],
  templateUrl: './collaborator-section.component.html',
  styleUrl: './collaborator-section.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CollaboratorSectionComponent {
  private featureFlagsService = inject(FeatureFlagsService);
  isRequestHeaderUiEnhancementEnabled = signal<boolean>(
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.workflow_header_ui_enhancement
    )
  );

  assignedList = input<Option[]>([]);
  rawCollaborators = input<Option[]>([]);
  assignedListItems = input<number>(0);
  assignedListTotalPages = input<number>(0);
  isAssignedListLoading = input<boolean>(false);
  assignedListCurrentPage = input<number>(1);

  // Lookup inputs (for inline add mode)
  lookupList = input<Option[]>([]);
  isLookupLoading = input<boolean>(false);
  lookupListCurrentPage = input<number>(1);
  lookupListTotalPages = input<number>(1);
  lookupListItems = input<number>(0);
  isAddingCollaborator = input<boolean>(false);

  openAssignCollaboratorsDialog = output<void>();
  updateAssignedList = output<{ currentPage: number; search: string }>();
  updateLookupList = output<{ currentPage: number; search: string }>();
  addCollaborator = output<Option>();
  deleteCollaborator = output<number>();

  isInAddMode = signal(false);

  private locallyAddedIds = signal<Set<string>>(new Set());

  localLookupList = computed(() => {
    const assignedIds = new Set(
      this.rawCollaborators().map(c => String(c.value))
    );
    const addedIds = this.locallyAddedIds();
    return this.lookupList().filter(
      item =>
        !assignedIds.has(String(item.value)) &&
        !addedIds.has(String(item.value))
    );
  });

  localLookupCount = computed(() =>
    Math.max(0, this.lookupListItems() - this.locallyAddedIds().size)
  );

  collaboratorDropdownList = computed(() => {
    return this.assignedList().map(item => ({
      id: item.value,
      name: item.label,
      image: item.image,
      assignable_type: '',
      job_title: item.job_title,
    }));
  });

  lookupDropdownList = computed(() => {
    return this.localLookupList().map(item => ({
      id: item.value,
      name: item.label,
      image: item.image,
      assignable_type: '',
      job_title: item.job_title,
    }));
  });

  getCollaboratorsDropdown(event: { currentPage: number; search: string }) {
    this.updateAssignedList.emit(event);
  }

  getLookupDropdown(event: { currentPage: number; search: string }) {
    this.updateLookupList.emit(event);
  }

  switchToAddMode() {
    this.isInAddMode.set(true);
    this.updateLookupList.emit({ currentPage: 1, search: '' });
  }

  onAddCollaborator(user: Option) {
    this.isInAddMode.set(true);
    const userId = String(user.id ?? user.value);
    this.locallyAddedIds.update(prev => {
      const next = new Set(prev);
      next.add(userId);
      return next;
    });
    this.addCollaborator.emit(user);
  }

  onDropdownOpenStateChange(isOpen: boolean) {
    if (!isOpen) {
      this.isInAddMode.set(false);
      this.locallyAddedIds.set(new Set());
    }
  }
}
